import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useAppDispatch } from '@/app/store';
import { userDetailsChanged } from '@/features/auth/store/authSlice';
import { profileService } from '../services/profileService';
import type {
  ProfileFormValues,
  SettingsFormValues,
  UserProfile,
  UserSettings,
} from '../types/profile.types';

export const profileKeys = {
  profile: ['profile', 'me'] as const,
  settings: ['profile', 'settings'] as const,
};

export function useProfileQuery(): UseQueryResult<UserProfile> {
  return useQuery({
    queryKey: profileKeys.profile,
    queryFn: () => profileService.getProfile(),
  });
}

export interface SettingsQueryOptions {
  /** Off until there is a session; the settings endpoint rejects anonymous callers. */
  readonly enabled?: boolean;
}

export function useSettingsQuery(
  options: SettingsQueryOptions = {}
): UseQueryResult<UserSettings> {
  return useQuery({
    queryKey: profileKeys.settings,
    queryFn: () => profileService.getSettings(),
    enabled: options.enabled ?? true,
  });
}

/**
 * Writes the server's response straight into the cache instead of invalidating.
 *
 * The update endpoint returns the saved record, so a refetch would ask for something we
 * already hold. Seeding the cache also means the form is never briefly repopulated with
 * the pre-save values while a refetch is in flight.
 *
 * The session user is patched at the same time. The signed-in identity is a copy of these
 * fields held in Redux, and leaving it behind means the header greets you by your old
 * name until the next sign-in.
 */
export function useUpdateProfile(): UseMutationResult<UserProfile, Error, ProfileFormValues> {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (values: ProfileFormValues) => profileService.updateProfile(values),
    onSuccess: (updated) => {
      queryClient.setQueryData(profileKeys.profile, updated);
      dispatch(userDetailsChanged(updated));
    },
  });
}

export function useUpdateSettings(): UseMutationResult<UserSettings, Error, SettingsFormValues> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: SettingsFormValues) => profileService.updateSettings(values),
    onSuccess: (updated) => {
      queryClient.setQueryData(profileKeys.settings, updated);
    },
  });
}
